import React from 'react';
import { Button } from 'antd';

/**
 * DAS-2871 step 3 — "Too early to tell".
 *
 * The honest majority view. Roughly 88% of the owner's alert types cannot be
 * judged yet: fewer than 10 of their alerts have reached the 6-month horizon.
 * The old screen showed those types with percentages anyway, which is exactly
 * how DAS-2869 happened (a wall of 100% figures, 18 of them resting on a
 * single alert).
 *
 * This view's whole job is expectation management, so it deliberately shows
 * NO percentages and NO verdicts — only how far each type is from being
 * judgeable. A progress count cannot be misread as a result.
 */

const AlertTooEarlyTab = ({ data, loading, onEdit, onShowEffectiveness }) => {
  if (loading) return <div style={{ padding: 24, color: '#888' }}>Loading…</div>;
  if (!data) {
    return (
      <div style={{ padding: 24, color: '#888' }}>
        Could not load this view. The other views are unaffected.
      </div>
    );
  }

  const immature = data.immature || [];
  const months = data.horizon_months || 6;
  const minAlerts = data.min_alerts || 10;
  const total = data.subtypes_total || 0;

  // "Closest to a verdict" is only meaningful when there is real progress to
  // report; a type with 1 matured alert is not "close". Half the floor is the
  // cut: 5+ matured shows in the list, the rest is a single honest count.
  const closest = immature.filter((r) => r.n_matured >= Math.ceil(minAlerts / 2)).slice(0, 8);
  const farAway = immature.length - closest.length;

  return (
    <div style={{ maxWidth: 820, color: '#1a1a1a' }}>
      <div
        style={{
          border: '1px solid #dbe4ee',
          background: '#f5f8fc',
          borderRadius: 10,
          padding: '16px 18px',
          marginBottom: 22,
          fontSize: 13.5,
          lineHeight: 1.65,
          color: '#333',
        }}
      >
        <strong>{immature.length} of your {total} active alert types cannot be judged yet.</strong>{' '}
        An alert only counts once it is {months} months old, and an alert type only gets a verdict
        once {minAlerts} of its alerts have reached that age. Until then this screen shows
        progress, not percentages — a young alert type with two lucky results is not a finding,
        it is a coin that landed heads twice.
      </div>

      {closest.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#3a5a8c', marginBottom: 8 }}>
            CLOSEST TO A VERDICT
          </div>
          <div style={{ border: '1px solid #e8e8e8', borderRadius: 10, overflow: 'hidden', marginBottom: 18 }}>
            {closest.map((r, i) => (
              <div
                key={`${r.identifier}-${r.alert_type}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  borderBottom: i === closest.length - 1 ? 'none' : '1px solid #f2f2f2',
                }}
              >
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: 14 }}>
                    {r.alert_subtype && r.alert_subtype !== 'Unknown' ? r.alert_subtype : 'combination'}
                  </div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    {r.alert_type} {r.identifier} · fired {r.n_fired} times in total
                  </div>
                  {r.wider_n >= 10 && (
                    <div data-testid="das2855-wider-line" style={{ fontSize: 12, color: '#3a5a8c' }}>
                      Wider test (other accounts): right {r.wider_wins} of {r.wider_n} ({r.wider_hr}%) vs the S&P 500
                    </div>
                  )}
                </div>

                <div style={{ width: 210, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, height: 8, background: '#eef1f5', borderRadius: 4, overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${Math.min(100, (r.n_matured / minAlerts) * 100)}%`,
                        height: '100%',
                        background: '#7da7d9',
                      }}
                    />
                  </div>
                  <span style={{ fontSize: 12.5, color: '#555', whiteSpace: 'nowrap' }}>
                    {r.n_matured} of {minAlerts}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: 6 }}>
                  <Button size="small" onClick={() => onEdit && onEdit(r.identifier)}>Edit</Button>
                  <Button size="small" onClick={() => onShowEffectiveness && onShowEffectiveness(r.identifier)}>
                    Effectiveness
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div
        style={{
          padding: '14px 16px',
          background: '#f6f6f6',
          borderRadius: 8,
          fontSize: 13,
          color: '#555',
          lineHeight: 1.6,
        }}
      >
        The remaining <strong style={{ color: '#1a1a1a' }}>{farAway}</strong> alert types have
        fewer than {Math.ceil(minAlerts / 2)} matured alerts — nothing useful to show yet. They
        are all still visible, with every number they do have, in the Full overview.
      </div>
    </div>
  );
};

export default AlertTooEarlyTab;
